import CollectionService from '@boServices/collectionService';
import { showToast } from '@sharedComponents/Toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const collectionService = new CollectionService();

const NO_OVERRIDES = [];

// Client and platform override rows of a collection. `reload()` fetches them again, e.g. after a scope
// change: the previous rows stay exposed until the new ones arrive, and are kept, with an error toast,
// when the refresh fails. Its promise never rejects: it settles once the latest refresh started since the
// call, whose rows include the change that called it, is exposed or its failure reported. `error` only
// reports a failed first load of the id.
const useClientOverrides = (id) => {
    const { t } = useTranslation('bo');
    // Last answer, tagged with its id: while another collection loads, nothing of the previous one is exposed.
    const [loaded, setLoaded] = useState({ id: null, overrides: NO_OVERRIDES, error: null });
    // Number of the latest refresh: a refresh overtaken by another one, by a change of id or by the end of
    // the page is discarded.
    const refreshRef = useRef(0);
    // Settling of the latest refresh of the current id, which an overtaken reload waits for.
    const latestRefreshRef = useRef(null);
    const currentIdRef = useRef(id);

    useEffect(() => {
        currentIdRef.current = id;
        // A late answer for a previous id must not overwrite the current rows.
        let cancelled = false;
        collectionService
            .getClientOverrides(id)
            .then((overrides) => {
                if (!cancelled) setLoaded({ id, overrides, error: null });
            })
            .catch((error) => {
                if (!cancelled) setLoaded({ id, overrides: NO_OVERRIDES, error });
            });
        return () => {
            cancelled = true;
            refreshRef.current += 1;
            latestRefreshRef.current = null;
        };
    }, [id]);

    const reload = useCallback(() => {
        // A reload kept from a previous collection, e.g. by a scope change that ends after a navigation, does nothing.
        if (currentIdRef.current !== id) return Promise.resolve();
        refreshRef.current += 1;
        const refresh = refreshRef.current;
        const settled = collectionService
            .getClientOverrides(id)
            .then((overrides) => {
                if (refresh === refreshRef.current) setLoaded({ id, overrides, error: null });
            })
            .catch(() => {
                if (refresh === refreshRef.current) {
                    showToast({ type: 'error', text: t('The access settings could not be refreshed. Please reload the page.') });
                }
            });
        latestRefreshRef.current = settled;
        // Once a refresh settles, a newer one started meanwhile is waited for in turn: the rows come from it.
        const untilLatest = (current) =>
            current.then(() => {
                const latest = latestRefreshRef.current;
                return latest && latest !== current ? untilLatest(latest) : undefined;
            });
        return untilLatest(settled);
    }, [id, t]);

    const isLoaded = loaded.id === id;

    return {
        overrides: isLoaded ? loaded.overrides : NO_OVERRIDES,
        loading: !isLoaded,
        error: isLoaded ? loaded.error : null,
        reload,
    };
};

export default useClientOverrides;
